import { useCallback, useEffect, useRef, useState } from 'react';
import { isIPhone } from 'lib/iphone';

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type OrientableScreen = ScreenOrientation & {
  lock?: (orientation: OrientationLockType) => Promise<void>;
};

function getFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function isFullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  /* iPhone Safari advertises webkitRequestFullscreen but it does not hide chrome. */
  if (isIPhone()) return false;
  const el = document.documentElement as FullscreenElement;
  return Boolean(el.requestFullscreen || el.webkitRequestFullscreen);
}

const FULLSCREEN_OPTS: FullscreenOptions = { navigationUI: 'hide' };

/** Chrome’s exit-fullscreen toast — grow the table only after this window. */
const NATIVE_FS_CHROME_HINT_MS = 5500;
const NATIVE_FS_TOAST_PX = 72;
const FS_LOCK_VH_VAR = '--tp-fs-lock-vh';
const FS_CHROME_INSET_VAR = '--tp-native-fs-chrome-inset';
const FS_HINT_CLASS = 'tp-native-fs-chrome-hint';

export function syncTableViewportHeight() {
  const lock = document.documentElement.style.getPropertyValue(FS_LOCK_VH_VAR).trim();
  if (lock) {
    document.documentElement.style.setProperty('--tp-vh', lock);
    return;
  }
  const vv = window.visualViewport;
  const useVisual = isIPhone() && vv != null;
  const heightPx = useVisual ? vv.height : window.innerHeight;
  document.documentElement.style.setProperty('--tp-vh', `${heightPx * 0.01}px`);
  if (useVisual) {
    document.documentElement.style.setProperty('--tp-vv-top', `${vv.offsetTop}px`);
  } else {
    document.documentElement.style.removeProperty('--tp-vv-top');
  }
}

function applyNativeFsHeightLock(preHeightPx: number) {
  const vh = `${preHeightPx * 0.01}px`;
  document.documentElement.style.setProperty(FS_LOCK_VH_VAR, vh);
  document.documentElement.style.setProperty('--tp-vh', vh);
  document.documentElement.classList.add(FS_HINT_CLASS);
  const extra = Math.max(0, window.innerHeight - preHeightPx);
  const padPx = Math.max(0, NATIVE_FS_TOAST_PX - extra);
  document.documentElement.style.setProperty(FS_CHROME_INSET_VAR, `${padPx}px`);
}

function clearNativeFsHeightLock() {
  document.documentElement.style.removeProperty(FS_LOCK_VH_VAR);
  document.documentElement.style.removeProperty(FS_CHROME_INSET_VAR);
  document.documentElement.classList.remove(FS_HINT_CLASS);
  try {
    document.documentElement.style.setProperty(
      '--tp-vh',
      `${window.innerHeight * 0.01}px`
    );
  } catch {
    /* ignore */
  }
}

async function requestElementFullscreen(element: HTMLElement): Promise<boolean> {
  const target = element as FullscreenElement;
  try {
    if (target.requestFullscreen) {
      await target.requestFullscreen(FULLSCREEN_OPTS);
    } else if (target.webkitRequestFullscreen) {
      await target.webkitRequestFullscreen();
    } else {
      return false;
    }
    return Boolean(getFullscreenElement());
  } catch {
    return false;
  }
}

/** Start native fullscreen in the same turn as the user gesture (required on mobile). */
function beginNativeFullscreen(element: HTMLElement): Promise<void> | null {
  const target = element as FullscreenElement;
  try {
    if (target.requestFullscreen) {
      return target.requestFullscreen(FULLSCREEN_OPTS);
    }
    if (target.webkitRequestFullscreen) {
      const result = target.webkitRequestFullscreen();
      return result instanceof Promise ? result : Promise.resolve();
    }
  } catch {
    return null;
  }
  return null;
}

function applyVisualImmersive(
  setIsImmersive: (value: boolean) => void
): boolean {
  setIsImmersive(true);
  collapseMobileBrowserChrome();
  return true;
}

async function exitDocumentFullscreen(): Promise<void> {
  const doc = document as FullscreenDocument;
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      await doc.webkitExitFullscreen();
    }
  } catch {
    /* ignore */
  }
}

function isPortraitViewport(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(orientation: portrait)').matches;
}

/** Keep the current orientation (portrait stay portrait; landscape stay landscape). */
async function lockCurrentOrientation(): Promise<void> {
  try {
    const orientation = window.screen.orientation as OrientableScreen | undefined;
    const lockType: OrientationLockType = isPortraitViewport() ? 'portrait' : 'landscape';
    await orientation?.lock?.(lockType);
  } catch {
    /* optional — lock is unavailable on many mobile browsers outside a PWA */
  }
}

async function unlockOrientation(): Promise<void> {
  try {
    window.screen.orientation?.unlock?.();
  } catch {
    /* optional */
  }
}

/** Nudge mobile browsers to collapse the address bar when API fullscreen is unavailable. */
function collapseMobileBrowserChrome(): void {
  try {
    if (isIPhone()) {
      window.scrollTo(0, 0);
      requestAnimationFrame(() => {
        window.scrollTo(0, 1);
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
          syncTableViewportHeight();
        });
      });
      return;
    }
    window.scrollTo(0, 1);
    setTimeout(() => window.scrollTo(0, 0), 50);
  } catch {
    /* ignore */
  }
}

export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(() => Boolean(getFullscreenElement()));
  const [isImmersive, setIsImmersive] = useState(false);
  /** Chrome’s “exit full screen” toast — pad the footer only while it is likely visible. */
  const [nativeFsChromeHint, setNativeFsChromeHint] = useState(false);
  const cssOnlyRef = useRef(false);
  const chromeHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preFsHeightRef = useRef<number | null>(null);
  const wasNativeFsRef = useRef(false);
  const supported = isFullscreenSupported();

  const clearChromeHintTimer = useCallback(() => {
    if (chromeHintTimerRef.current) {
      clearTimeout(chromeHintTimerRef.current);
      chromeHintTimerRef.current = null;
    }
  }, []);

  const releaseNativeFsChromeHint = useCallback(() => {
    clearChromeHintTimer();
    preFsHeightRef.current = null;
    clearNativeFsHeightLock();
    setNativeFsChromeHint(false);
  }, [clearChromeHintTimer]);

  const armNativeFsChromeHint = useCallback(() => {
    clearChromeHintTimer();
    const pre = preFsHeightRef.current ?? window.innerHeight;
    preFsHeightRef.current = pre;
    applyNativeFsHeightLock(pre);
    requestAnimationFrame(() => applyNativeFsHeightLock(pre));
    setNativeFsChromeHint(true);
    chromeHintTimerRef.current = setTimeout(() => {
      chromeHintTimerRef.current = null;
      preFsHeightRef.current = null;
      clearNativeFsHeightLock();
      setNativeFsChromeHint(false);
    }, NATIVE_FS_CHROME_HINT_MS);
  }, [clearChromeHintTimer]);

  useEffect(() => {
    const onChange = () => {
      const active = Boolean(getFullscreenElement());
      setIsFullscreen(active);
      if (active) {
        cssOnlyRef.current = false;
        setIsImmersive(true);
        if (!wasNativeFsRef.current) {
          armNativeFsChromeHint();
        }
        wasNativeFsRef.current = true;
        void lockCurrentOrientation();
      } else if (!cssOnlyRef.current) {
        wasNativeFsRef.current = false;
        setIsImmersive(false);
        releaseNativeFsChromeHint();
        void unlockOrientation();
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    onChange();
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      clearChromeHintTimer();
    };
  }, [armNativeFsChromeHint, releaseNativeFsChromeHint, clearChromeHintTimer]);

  useEffect(() => {
    if (!isImmersive) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = '';
    };
  }, [isImmersive]);

  const enterFullscreen = useCallback((element: HTMLElement | null): Promise<boolean> => {
    if (!element) return Promise.resolve(false);

    const iphone = isIPhone();
    if (!iphone) {
      preFsHeightRef.current = window.innerHeight;
      applyNativeFsHeightLock(preFsHeightRef.current);
      setNativeFsChromeHint(true);
    }

    const markNative = async (): Promise<boolean> => {
      if (!getFullscreenElement()) return false;
      cssOnlyRef.current = false;
      await lockCurrentOrientation();
      setIsFullscreen(true);
      setIsImmersive(true);
      armNativeFsChromeHint();
      return true;
    };

    const fallbackCss = (): boolean => {
      cssOnlyRef.current = true;
      releaseNativeFsChromeHint();
      return applyVisualImmersive(setIsImmersive);
    };

    if (supported) {
      const nativeTarget = document.documentElement;
      const nativeRequest =
        beginNativeFullscreen(nativeTarget) ?? beginNativeFullscreen(element);
      if (nativeRequest) {
        return nativeRequest
          .then(async () => {
            if (await markNative()) return true;
            const pageEntered = await requestElementFullscreen(element);
            if (pageEntered) return markNative();
            return fallbackCss();
          })
          .catch(async () => {
            const pageEntered = await requestElementFullscreen(element);
            if (pageEntered) return markNative();
            return fallbackCss();
          });
      }
    }

    return Promise.resolve(fallbackCss());
  }, [supported, armNativeFsChromeHint, releaseNativeFsChromeHint]);

  const exitFullscreen = useCallback(async () => {
    cssOnlyRef.current = false;
    setIsFullscreen(false);
    setIsImmersive(false);
    releaseNativeFsChromeHint();
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    await exitDocumentFullscreen();
    await unlockOrientation();
  }, [releaseNativeFsChromeHint]);

  return {
    isFullscreen,
    isImmersive,
    isImmersiveActive: isFullscreen || isImmersive,
    nativeFsChromeHint,
    supported,
    enterFullscreen,
    exitFullscreen,
  };
}
